import type { MutableRefObject, Ref } from 'react';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import type { FlatPickrProps, FlatPickrRefType } from 'react-flatpickr';
import FlatPickr from 'react-flatpickr';
import flatpickr from 'flatpickr';
import * as locales from 'flatpickr/dist/l10n';
import type { FocusTrap } from 'focus-trap';
import { createFocusTrap } from 'focus-trap';
import { TextInput } from './TextInput';
import type { ClassNameProp, ValidIconName } from '..';
import { Tool } from '..';
import type { CommonInputProps } from './CommonInputProps';
import {
  handleOnKeyDown,
  isEmptyArray,
  isEmptyString,
  removeSecondsFromDate
} from '../../util';
import type { ArrayIfMultiElseNullable } from '../../lib';
import {
  customDateFormatter,
  DefaultDateFormatter as dateFormatter,
  getDefaultDateFormattingOptions,
  getDefaultTimeFormattingOptions,
  getLocaleDateFormat,
  intl,
  parseDateWithOptions
} from '../../lib';

// Fallback for the storybook
const locale = (window.SenseConstants?.language ??
  'en') as flatpickr.Options.LocaleKey;
// Localize all date pickers
flatpickr.localize(locales.default[locale]);

/**
 * A little helper function to filter empty strings, undefined and null out of a given array.
 *
 * @author agd
 * @param value the value to filter
 * @return The filtered array. If the given value is not an array, then it will just return the value as is
 */
function filterNullishValuesEmptyStringsIfArray<
  T extends string | number | Date | null | undefined
>(value: Array<T> | T): Array<T> | T {
  return Array.isArray(value)
    ? value.filter(v => v !== null && v !== undefined && v !== '')
    : value;
}

/**
 * Common props for all date/time pickers.
 *
 * @author Bennett Piater
 */
export interface DateTimePickerProps<Variant extends 'single' | 'multi'>
  extends ClassNameProp,
    Pick<
      flatpickr.Options.Options,
      'allowInput' | 'inline' | 'minDate' | 'maxDate'
    >,
    Pick<
      CommonInputProps<string>,
      | 'label'
      | 'error'
      | 'onBlur'
      | 'required'
      | 'disabled'
      | 'labelOnTop'
      | 'readOnly'
    > {
  /**
   * Callback to fire whenever the selection changes.<br>
   * It receives an array of selected dates (important for multi-/range-selection) and the current input text value.
   * {@link https://flatpickr.js.org/events/#onchange}
   */
  onChange: (selectedDates: ArrayIfMultiElseNullable<Variant, Date>) => void;
  /**
   * The state value of the component. Setting this will result this being intended to be a controlled component.
   * When passing a value, then passing an onChange handler is expected since the onChange handler is supposed to update the input state in that case.
   */
  value: ArrayIfMultiElseNullable<Variant, Date | string | number>;
  /**
   * Whether FlatPickr graphical user interface can or cannot be opened.
   * <li>Default: can be opened -> tool button is present at right side</li>
   * <li>Cannot be opened -> just date icon is present at right side</li>
   */
  hidePicker?: boolean;
  labelOnTop?: boolean;
}

const formatDate = dateFormatter.formatDate.bind(dateFormatter);
const parseDate = (input: string) => parseDateWithOptions(input, dateFormatter);

const datetimeFormatter = customDateFormatter({
  ...getDefaultDateFormattingOptions(),
  ...getDefaultTimeFormattingOptions(),
  second: undefined
});
const datetimePlaceholder = getLocaleDateFormat(datetimeFormatter)
  .split(' ')
  .map(datePart =>
    datePart.includes('HH') ? datePart.toLowerCase() : datePart.toUpperCase()
  )
  .join(' ');
const formatDatetime = datetimeFormatter.formatDate.bind(datetimeFormatter);
const parseDatetime = (input: string) =>
  parseDateWithOptions(input, datetimeFormatter);

const timeFormatter = customDateFormatter({
  ...getDefaultTimeFormattingOptions(),
  second: undefined
});
const timePlaceholder = getLocaleDateFormat(timeFormatter).toLowerCase();
const formatTime = timeFormatter.formatDate.bind(timeFormatter);
const parseTime = (input: string) => parseDateWithOptions(input, timeFormatter);

//Setting the icon to a hyphen, so we don't have to translate 'to' in different languages
flatpickr.l10ns.default.rangeSeparator = ' - ';
const rangePlaceholder = Array(2)
  .fill(getLocaleDateFormat(dateFormatter))
  .join(flatpickr.l10ns.default.rangeSeparator)
  .toUpperCase();

/**
 * A custom hook to create a focus-trap for a flatpickr instance.
 *
 * @author agd
 * @param props important picked props from {@link DateTimePickerProps}. If the picker is inline, then no focus-trap is needed.
 * @return properties that can easily be destructured into a {@link FlatPickr} component's props
 */
function useDatePickerFocusTrap({
  onBlur,
  inline
}: Pick<DateTimePickerProps<never>, 'onBlur' | 'inline' | 'allowInput'>): Pick<
  FlatPickrProps,
  'onClose' | 'onOpen' | 'ref'
> {
  // necessary refs
  const pickerRef = useRef<FlatPickrRefType | null>(null);
  const focusTrap = useRef<FocusTrap | null>(null);

  const pickerExists = pickerRef.current;
  // set up the focusTrap on the flatpickr ref
  useEffect(() => {
    if (!focusTrap.current && pickerRef.current) {
      focusTrap.current = createFocusTrap(
        [
          // it is entirely possible that pickerRef.current.flatpickr could end up being undefined
          pickerRef.current?.flatpickr?.calendarContainer,
          // find the closest parent container which makes sense as tabbable container, with a final fallback to simply the document.body
          pickerRef.current?.node?.form ??
            pickerRef.current?.node?.closest<HTMLElement>(
              '.ehs-modal, .ehs-panel, .portlet-boundary'
            ) ??
            document.body
        ],
        {
          escapeDeactivates: false,
          // needed, since otherwise the <input> will be focused again, causing the picker to be opened again
          returnFocusOnDeactivate: false
        }
      );
    }
  }, [pickerExists]);

  // clean-up just during mount state
  useEffect(function () {
    return function () {
      // deactivate the trap if we had created it
      if (focusTrap.current) {
        focusTrap.current.deactivate();
        focusTrap.current = null;
      }
    };
  }, []);

  return {
    ref: pickerRef,
    onClose: () => {
      focusTrap.current?.deactivate();
      onBlur?.();
    },
    onOpen: () => {
      // check if it's not inline, just to be safe
      if (!inline) {
        focusTrap.current?.activate();
      }
      const selectedDateElem = pickerRef.current?.flatpickr.selectedDateElem;
      if (selectedDateElem?.isConnected) {
        selectedDateElem.focus();
      } else {
        pickerRef.current?.flatpickr.todayDateElem?.focus();
      }
    }
  };
}

function renderDatePickerInput({
  pickerRef,
  className,
  disabled,
  readOnly,
  error,
  hidePicker,
  label,
  required,
  labelOnTop
}: {
  className: string | undefined;
  disabled: boolean | undefined;
  readOnly: boolean | undefined;
  hidePicker: boolean | undefined;
  error: string | undefined;
  inputChange?: (value: Date | null) => void;
  label: string;
  pickerRef?: Ref<FlatPickrRefType>;
  required: boolean | undefined;
  labelOnTop?: boolean;
}) {
  let icon: ValidIconName | JSX.Element = 'calendar';
  if (hidePicker !== true && !disabled && !readOnly) {
    icon = (
      <Tool
        className="right"
        icon={icon}
        onClick={() =>
          (pickerRef as MutableRefObject<FlatPickrRefType>)?.current.flatpickr.open()
        }
        tooltip={intl('view.button.open')}
      />
    );
  }

  return function DatePickerRenderer(
    { placeholder, value }: Pick<FlatPickrProps, 'placeholder' | 'value'>,
    ref: Ref<HTMLInputElement>
  ): JSX.Element {
    // set value empty state to TextInput, since change listener does not actually get fired when the flatpickr is updated.
    let valueEmpty: boolean | undefined =
      (typeof value === 'string' && isEmptyString(value)) ||
      (Array.isArray(value) &&
        isEmptyArray(value.filter(v => v !== undefined && v !== null)));
    if (!valueEmpty && (value === undefined || value === null)) {
      valueEmpty = undefined;
    }

    return (
      <TextInput
        className={className}
        disabled={disabled}
        readOnly={readOnly}
        error={error}
        label={label}
        placeholder={placeholder}
        ref={ref}
        required={required}
        type="text"
        valueEmpty={valueEmpty}
        rightIcon={icon}
        labelOnTop={labelOnTop}
        onKeyDown={event => {
          if (event.key === 'Enter') {
            // keeps input element focused and also triggers form submit
            (event.target as HTMLInputElement).focus();
          }
        }}
      />
    );
  };
}

/**
 * A simple time picker.
 *
 * @param props {@link DateTimePickerProps}
 */
export function TimePicker({
  label,
  value,
  className,
  disabled,
  readOnly,
  error,
  hidePicker,
  required,
  onBlur,
  // extract unused onChange from options
  onChange,
  labelOnTop,
  ...options
}: DateTimePickerProps<'single'>): JSX.Element {
  const focusTrapProps = useDatePickerFocusTrap({
    onBlur,
    inline: options.inline
  });

  const onChangeCallback = useCallback(
    values => onChange(values?.[0] ?? null),
    [onChange]
  );

  return (
    <FlatPickr
      {...focusTrapProps}
      disabled={disabled}
      readOnly={!(options.allowInput ?? true)}
      placeholder={timePlaceholder}
      options={{
        ...options,
        clickOpens: options.allowInput === false,
        disableMobile: true,
        enableTime: true,
        noCalendar: true,
        allowInvalidPreload: true,
        formatDate: formatTime,
        parseDate: parseTime,
        onKeyDown: onKeyDownHandler(options),
        allowInput: options.allowInput ?? true,
        onChange: onChangeCallback
      }}
      render={renderDatePickerInput({
        pickerRef: focusTrapProps.ref,
        className,
        disabled,
        readOnly,
        hidePicker,
        error,
        label,
        required,
        labelOnTop
      })}
      // TODO: FlatPickr issue with mobile view https://github.com/flatpickr/flatpickr/issues/2246
      value={removeSecondsFromDate(value)}
    />
  );
}

/**
 * Props for the date picker.
 */
export interface DatePickerProps<Variant extends 'single' | 'multi'>
  extends DateTimePickerProps<Variant> {
  /**
   * Whether single or multiple selection is used.
   */
  variant: Variant;
}

/**
 * A date picker.<br>
 * It also supports multi-selection.
 *
 * @param props {@link DatePickerProps}
 */
export function DatePicker<Variant extends 'single' | 'multi'>(
  props: DatePickerProps<Variant> & {
    variant: 'single';
    value: Date | null;
    onChange: (selectedDate: Date | null) => void;
    labelOnTop?: boolean;
  }
): JSX.Element;

export function DatePicker<Variant extends 'single' | 'multi'>(
  props: DatePickerProps<Variant> & {
    variant: 'multi';
    value: Date[];
    onChange: (selectedDates: Date[]) => void;
    labelOnTop?: boolean;
  }
): JSX.Element;

export function DatePicker<Variant extends 'single' | 'multi'>({
  variant,
  className,
  disabled,
  readOnly,
  error,
  hidePicker,
  label,
  required,
  value,
  onBlur,
  onChange,
  labelOnTop,
  ...options
}: DatePickerProps<Variant>): JSX.Element {
  const focusTrapProps = useDatePickerFocusTrap({
    onBlur,
    inline: options.inline
  });
  const placeholder = useMemo(() => {
    const dateFormat = getLocaleDateFormat(dateFormatter).toUpperCase();
    if (variant === 'multi') {
      return Array(2).fill(dateFormat).concat('...').join(', ');
    }
    return dateFormat;
  }, [variant]);
  const onChangeCallback = useCallback(
    values => onChange(variant === 'multi' ? values : values?.[0] ?? null),
    [onChange, variant]
  );

  return (
    <FlatPickr
      {...focusTrapProps}
      disabled={disabled}
      placeholder={placeholder}
      readOnly={!(options.allowInput ?? true)}
      onChange={onChangeCallback}
      options={{
        ...options,
        clickOpens: options.allowInput === false,
        disableMobile: true,
        mode: variant === 'multi' ? 'multiple' : 'single',
        allowInvalidPreload: true,
        formatDate,
        // NOTE: this ignores the formatting options from flatpickr, but it should match our formatDate...
        parseDate,
        onKeyDown: onKeyDownHandler(options),
        allowInput: options.allowInput ?? true
      }}
      render={renderDatePickerInput({
        pickerRef: focusTrapProps.ref,
        hidePicker,
        className,
        disabled,
        readOnly,
        error,
        label,
        required,
        labelOnTop
      })}
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      value={filterNullishValuesEmptyStringsIfArray<typeof value>(value)}
    />
  );
}

/**
 * Correct the times to maximize the range, fulfilling human expectations.
 */
function correctRangeTimes(values: Array<Date>): Array<Date> {
  if (isEmptyArray(values)) return values;
  const [from, to] = values;
  from.setHours(0, 0, 0, 0);
  if (!to) {
    return [from];
  }
  to.setHours(23, 59, 59, 999);

  return [from, to];
}

/**
 * A picker for date ranges.
 *
 * @param params {@link DateTimePickerProps}
 */
export function DateRangePicker({
  label,
  value,
  className,
  disabled,
  readOnly,
  error,
  hidePicker,
  required,
  onBlur,
  onChange,
  ...options
}: DateTimePickerProps<'multi'>): JSX.Element {
  const focusTrapProps = useDatePickerFocusTrap({
    onBlur,
    inline: options.inline
  });
  const onChangeCallback = useCallback(
    values => onChange(correctRangeTimes(values)),
    [onChange]
  );

  return (
    <FlatPickr
      {...focusTrapProps}
      disabled={disabled}
      placeholder={rangePlaceholder}
      readOnly={!(options.allowInput ?? true)}
      onChange={onChangeCallback}
      options={{
        ...options,
        clickOpens: options.allowInput === false,
        disableMobile: true,
        mode: 'range',
        allowInvalidPreload: true,
        formatDate,
        parseDate,
        onKeyDown: onKeyDownHandler(options),
        allowInput: options.allowInput ?? true
      }}
      render={renderDatePickerInput({
        pickerRef: focusTrapProps.ref,
        className,
        disabled,
        readOnly,
        error,
        hidePicker,
        label,
        required
      })}
      value={filterNullishValuesEmptyStringsIfArray(value)}
    />
  );
}

/**
 * A combined date/time picker.
 *
 * @param params {@link DateTimePickerProps}
 */
export function DateTimePicker({
  className,
  disabled,
  readOnly,
  error,
  hidePicker,
  label,
  required,
  value,
  onBlur,
  onChange,
  labelOnTop,
  ...options
}: DateTimePickerProps<'single'>): JSX.Element {
  const focusTrapProps = useDatePickerFocusTrap({
    onBlur,
    inline: options.inline
  });
  const onChangeCallback = useCallback(
    values => onChange(values?.[0] ?? null),
    [onChange]
  );

  return (
    <FlatPickr
      {...focusTrapProps}
      disabled={disabled}
      placeholder={datetimePlaceholder}
      readOnly={!((options.allowInput || readOnly) ?? true)}
      onChange={onChangeCallback}
      options={{
        ...options,
        clickOpens: options.allowInput === false,
        disableMobile: true,
        enableTime: true,
        allowInvalidPreload: true,
        formatDate: formatDatetime,
        parseDate: parseDatetime,
        onKeyDown: onKeyDownHandler(options),
        allowInput: options.allowInput ?? !readOnly
      }}
      render={renderDatePickerInput({
        pickerRef: focusTrapProps.ref,
        className,
        disabled,
        readOnly,
        error,
        hidePicker,
        label,
        required,
        labelOnTop
      })}
      // TODO: FlatPickr issue with mobile view https://github.com/flatpickr/flatpickr/issues/2246
      value={removeSecondsFromDate(value)}
    />
  );
}

function onKeyDownHandler(options: FlatPickrProps['options']) {
  return (
    _selectedDates: Date[],
    _dateStr: string,
    instance: flatpickr.Instance,
    event: KeyboardEvent
  ) => handleOnKeyDown(instance, event, options);
}
